'use client';

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import GlobalTitle from "@shared/GlobalTitle";
import { testimonialsData } from "@data/testimonialsData";
import { clientLogos } from "@data/clientLogoData";
import { cn } from "@/lib/utils";
import { Carousel, CarouselContent, CarouselItem } from "@ui/carousel";
import Autoplay from "embla-carousel-autoplay";

const logoPlugins = [
  Autoplay({ delay: 1800, stopOnInteraction: false, stopOnMouseEnter: true }),
];

const logoMask = 'linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%)';

function StarRating({ count = 5 }) {
  return (
    <div className="flex gap-0.5 mb-4">
      {Array.from({ length: count }).map((_, i) => (
        <Star key={i} size={16} className="fill-amber-400 text-amber-400" />
      ))}
    </div>
  );
}

function getInitials(name) {
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

function TestimonialCard({ item }) {
  return (
    <div className="relative bg-card rounded-2xl border border-border/40 p-7 flex flex-col h-full">
      <span className="absolute top-1 right-4 text-5xl font-sans leading-none text-border select-none">
        &ldquo;&rdquo;
      </span>
      {item.rating > 0 && <StarRating count={item.rating} />}
      <p className="text-sm text-muted-foreground leading-relaxed flex-1 mb-4">
        &ldquo;{item.quote}&rdquo;
      </p>
      {item.result && (
        <p className="font-semibold text-ink mb-5 border-l-2 border-primary pl-3">
          {item.result}
        </p>
      )}
      <div className="flex items-center gap-3 pt-4 border-t border-border/40">
        {item.author.image ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={item.author.image}
            alt={item.author.name}
            className="w-10 h-10 rounded-full object-cover shrink-0 border border-border/40"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shrink-0">
            <span className="text-white text-xs font-bold tracking-wide">
              {getInitials(item.author.name)}
            </span>
          </div>
        )}
        <div>
          <p className="font-bold text-sm text-ink">{item.author.name}</p>
          <p className="text-xs text-muted-foreground">
            {[item.author.role, item.author.company].filter(Boolean).join(" · ")}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function TestimonialsSection({
  data = testimonialsData,
  topText,
  titleText,
  coloredText,
  description,
  className,
}) {
  const items = data.items ?? [];
  const heading = data.heading ?? {};

  const resolvedTopText = topText ?? heading.topText;
  const resolvedTitleText = titleText ?? heading.titleText;
  const resolvedColoredText = coloredText ?? heading.coloredText;
  const resolvedDescription = description ?? heading.description;

  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on("select", onSelect);
    return () => api.off("select", onSelect);
  }, [api]);

  return (
    <section className={cn("py-12 md:py-20 bg-white overflow-hidden", className)}>
      <div className="max-w-[1400px] mx-auto px-6">

        {/* Header */}
        <div className="text-center mb-4">
          <GlobalTitle
            topText={resolvedTopText}
            titleText={resolvedTitleText}
            coloredText={resolvedColoredText}
            classes="!text-left md:!text-center md:items-center items-start"
          />
          <p className="text-muted-foreground max-w-4xl mx-auto">
            {resolvedDescription}
          </p>
        </div>

        {/* Carousel with side gradient fade */}
        <div className="relative py-4">
          <div className="hidden sm:block pointer-events-none absolute inset-y-0 left-0 w-28 z-10 bg-linear-to-r from-[#ffffff] to-transparent" />
          <div className="hidden sm:block pointer-events-none absolute inset-y-0 right-0 w-28 z-10 bg-linear-to-l from-[#ffffff] to-transparent" />

          <Carousel
            setApi={setApi}
            opts={{ loop: true, align: "center" }}
            className="w-full **:data-[slot=carousel-content]:overflow-x-clip **:data-[slot=carousel-content]:overflow-y-visible"
          >
            <CarouselContent className="-ml-4">
              {items.map((item, i) => (
                <CarouselItem
                  key={item.id}
                  className="pl-4 basis-full md:basis-[70%] lg:basis-[55%]"
                >
                  <div className={`transition-all duration-500 ease-out h-full rounded-2xl ${i === current ? "scale-100 shadow-2xl shadow-black/15" : "scale-[0.92] shadow-none"}`}>
                    <TestimonialCard item={item} />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>

        {/* Controls: prev · dots · next */}
        <div className="flex items-center justify-center gap-5 mt-8">
          <button
            onClick={() => api?.scrollPrev()}
            aria-label="Previous"
            className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-colors duration-200 cursor-pointer"
          >
            <ChevronLeft size={18} />
          </button>

          <div className="flex items-center">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => api?.scrollTo(i)}
                aria-label={`Slide ${i + 1}`}
                className="p-2 min-w-6 min-h-6 flex items-center justify-center cursor-pointer"
              >
                <span
                  className={`block h-1.5 rounded-full transition-all duration-300 ${i === current ? "w-6 bg-primary" : "w-1.5 bg-border"
                    }`}
                />
              </button>
            ))}
          </div>

          <button
            onClick={() => api?.scrollNext()}
            aria-label="Next"
            className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-colors duration-200 cursor-pointer"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Client Logo Carousel */}
        <div className="mt-12 overflow-hidden">
          <p className="text-center text-sm font-semibold tracking-widest uppercase text-muted-foreground mb-8">
            Building Success Together
          </p>
          <div style={{ maskImage: logoMask, WebkitMaskImage: logoMask }}>
            <Carousel
              opts={{ loop: true, align: 'start', dragFree: true }}
              plugins={logoPlugins}
            >
              <CarouselContent className="-ml-5">
                {clientLogos.map((logo) => (
                  <CarouselItem key={logo.name} className="pl-3 basis-1/3 md:basis-1/4 lg:basis-1/5">
                    <div className="group flex items-center justify-center h-14 px-4 rounded-xl border border-border/40 bg-gray-50/80 hover:bg-white hover:border-primary/25 hover:shadow-sm transition-all duration-200">
                      <Image
                        src={logo.src}
                        alt={logo.name}
                        width={160}
                        height={32}
                        className={`${logo.height ?? 'h-6'} w-auto object-contain opacity-85 grayscale-1 group-hover:opacity-100 group-hover:grayscale-0 transition-all duration-300`}
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
        </div>

        <p className="text-center text-muted-foreground mt-8 text-sm md:text-base">Want to hear more from our clients?{' '}
          <Link className="text-primary font-semibold underline underline-offset-4 hover:text-primary/80 transition-colors" href="/client-reviews">Read all reviews</Link>
          {' '}and see what they have to say.
        </p>
      </div>
    </section>
  );
}
